import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useSandboxPassIdentity } from '../../app/SandboxPassContext.jsx';
import { resolveLanding } from '../../lib/onboardingState.js';
import { useClientConfig } from '../../lib/clientConfig.js';
import { api } from '../../lib/apiClient.js';
import { clearSandboxPass } from './sandboxPass.js';
import { guideReturnToFromSearch } from './guideReturnTo.js';
import { PrimeSummoningIntro } from './PrimeSummoning/PrimeSummoningIntro.jsx';
import { LoginBrandLockup } from './PrimeSummoning/LoginBrandLockup.jsx';
import './personaLogin.css';
import './auth.redesign.css';

const PERSONAS = [
  { key: 'executive_sponsor', highlight: 'Executive pulse · mission impact · AI briefings' },
  { key: 'delivery_leader', highlight: 'Sprint health · roadmap · gate readiness' },
  { key: 'data_steward', highlight: 'Connectors · lineage · audit · reconciliation' },
  { key: 'pending_access', highlight: 'eSAAR draft & status — limited surface' },
  { key: 'system_admin', highlight: 'RBAC inspection · audit · workspace controls' },
];

function authConfigValue(config, key, fallback) {
  if (config?.auth && key in config.auth) return config.auth[key];
  if (config?.flags && key in config.flags) return config.flags[key];
  return fallback;
}

function normalizeOrigin(origin) {
  return String(origin || '')
    .trim()
    .replace(/\/+$/, '');
}

export function PersonaLoginPage() {
  const isProductionBuild = import.meta.env.PROD;
  const { setPersona, loginWithPassword, loginWithCac } = useCurrentUser();
  const clientConfig = useClientConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const passIdentity = useSandboxPassIdentity();
  const guideReturnTo = guideReturnToFromSearch(location.search);

  // The hero's nested-diamond mark is a vector component and is always in the
  // DOM. The summoning layer plays over this slot and then hands off to it, so
  // the only thing gated here is whether the mark is briefly held back while
  // the summon is still running — never whether the page has a brand.
  const brandSlotRef = useRef(null);
  const [markVisible, setMarkVisible] = useState(false);
  const onIntroSettled = useCallback(() => setMarkVisible(true), []);
  const summoning = <PrimeSummoningIntro slotRef={brandSlotRef} onSettled={onIntroSettled} />;

  const personaShortcutsEnabled = authConfigValue(clientConfig, 'personaShortcutsEnabled', true);
  const cacAvailable = authConfigValue(clientConfig, 'cacAvailable', false);
  const sandboxMode = clientConfig?.sandbox?.mode || clientConfig?.flags?.sandboxMode || 'disabled';
  const sandboxFrontDoorEnabled =
    clientConfig?.sandbox?.frontDoorEnabled === true ||
    clientConfig?.flags?.sandboxFrontDoorEnabled === true;
  const sandboxInstance = clientConfig?.sandbox?.sandbox || null;
  const sharedLiveMode = sandboxMode === 'disabled';
  const passwordOnlySandboxLogin = sandboxMode === 'sandbox';
  const adminOrigin = clientConfig?.sandbox?.adminOrigin || null;
  const onDedicatedAdminHost =
    sandboxFrontDoorEnabled &&
    typeof window !== 'undefined' &&
    normalizeOrigin(window.location.origin) &&
    normalizeOrigin(window.location.origin) === normalizeOrigin(adminOrigin);

  async function routeAuthenticatedUser(result, fallbackUserId, fallbackLanding) {
    const defaultLanding = result?.defaultLanding || fallbackLanding || '/home';
    const userId = result?.actorUserId || fallbackUserId || null;
    if (result?.mustChangePassword) {
      navigate(
        guideReturnTo
          ? `/change-password?returnTo=${encodeURIComponent(guideReturnTo)}`
          : '/change-password',
      );
      return;
    }
    if (guideReturnTo) {
      window.location.assign(guideReturnTo);
      return;
    }
    navigate(resolveLanding(defaultLanding, userId));
  }

  async function enter(personaKey) {
    setBusy(`persona:${personaKey}`);
    setErr(null);
    try {
      const res = await setPersona(personaKey);
      await routeAuthenticatedUser(
        res,
        rbac.PERSONA_TO_USER_ID[personaKey],
        rbac.PERSONA_DEFAULT_ROUTE[personaKey] || '/home',
      );
    } catch (e) {
      setErr(e);
      setBusy(null);
    }
  }

  async function handlePasswordSignIn(event) {
    event.preventDefault();
    setBusy('password');
    setErr(null);
    try {
      // On a private sandbox the sign-in identity is fixed by the verified
      // routing pass; everywhere else the typed email is used.
      const signInEmail = passwordOnlySandboxLogin ? passIdentity?.email || email : email;
      const res = await loginWithPassword({ email: signInEmail, password });
      if (passIdentity) clearSandboxPass();
      await routeAuthenticatedUser(res, res?.actorUserId, res?.defaultLanding || '/home');
    } catch (error) {
      setErr(error);
      setBusy(null);
    }
  }

  async function handleCacSignIn() {
    setBusy('cac');
    setErr(null);
    try {
      const res = await loginWithCac();
      if (passIdentity) clearSandboxPass();
      await routeAuthenticatedUser(res, res?.actorUserId, res?.defaultLanding || '/home');
    } catch (error) {
      setErr(error);
      setBusy(null);
    }
  }

  async function handleFrontDoorSubmit(event) {
    event.preventDefault();
    setBusy('frontdoor');
    setErr(null);
    try {
      const res = await api.post('/sandbox/resolve', { email });
      const resolution = res?.data?.resolution || res?.resolution || null;
      if (resolution === 'root_admin_cac') {
        window.location.assign(res?.data?.adminLoginUrl || res?.adminLoginUrl);
        return;
      }

      if (resolution === 'request_access') {
        window.location.assign(res?.data?.requestAccessUrl || res?.requestAccessUrl);
        return;
      }

      const loginUrl = res?.data?.loginUrl || res?.loginUrl;
      if (resolution !== 'sandbox' && !loginUrl) {
        throw new Error('The sandbox assignment did not include a login URL.');
      }
      if (guideReturnTo) {
        const sandboxLogin = new URL(loginUrl, window.location.origin);
        sandboxLogin.searchParams.set('returnTo', guideReturnTo);
        window.location.assign(sandboxLogin.toString());
      } else {
        window.location.assign(loginUrl);
      }
    } catch (error) {
      setErr(error);
      setBusy(null);
    }
  }

  if (clientConfig.loading) {
    return (
      <div className="persona-login-shell">
        <div className="persona-login-card">
          <div className="empty-state">Loading sign-in…</div>
        </div>
      </div>
    );
  }

  if (sandboxFrontDoorEnabled) {
    const rootAdminCacStep = onDedicatedAdminHost;
    // On the dedicated admin host the operator arrives with a verified admin
    // routing pass; surface that identity in the panel copy when present.
    const adminIdentityEmail = passIdentity?.email || email;

    return (
      <div className="persona-login-shell">
        {summoning}
        <div className="persona-login-hero">
          {/* The hero holds still: only the sign-in rail scrolls, so the brand
              stays on screen while the form is used. This wrapper is the hero's
              content block — the hero itself is the (rarely used) scrollport for
              viewports too short to fit the identity. */}
          <div className="persona-login-hero-inner">
            <div className="login-brand">
              {/* PRIME is the primary brand (PRIME-BRAND-UX-001); the DHA emblem
                is a secondary affiliation below the headline. */}
              <LoginBrandLockup
                kicker="Private UAT entry"
                slotRef={brandSlotRef}
                markVisible={markVisible}
              />
              <div className="login-affiliation" data-testid="login-affiliation">
                <span className="login-brand-mark login-brand-seal">
                  <img
                    src="/brand/dha-seal.svg"
                    alt="Defense Health Agency seal"
                    data-testid="login-dha-seal"
                  />
                </span>
                <span className="login-affiliation-text">Defense Health Agency</span>
              </div>
            </div>
            <p className="login-blurb">
              {rootAdminCacStep
                ? 'This PMHub admin host is reserved for the registered root admin operator. Continue with CAC to reach the administration shell.'
                : 'Enter the email address your coordinator registered for UAT and PRIME will route you to your assigned private sandbox automatically.'}
            </p>
            <ul className="login-bullets">
              {rootAdminCacStep ? (
                <>
                  <li>CAC sign-in starts only on this dedicated admin host</li>
                  <li>
                    Tester email routing and Request access stay on the shared public PMHub host
                  </li>
                  <li>
                    Password management, approvals, and governance actions remain unchanged after
                    sign-in
                  </li>
                </>
              ) : (
                <>
                  <li>One shared sign-in entry point; no environment picker</li>
                  <li>Your testing work stays inside your assigned sandbox</li>
                  <li>Unknown emails go to Request access instead of the CAC approval flow</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="persona-login-card">
          {rootAdminCacStep ? (
            <section className="auth-entry-panel">
              <div className="auth-entry-copy">
                <span className="eyebrow">Administrator access</span>
                <h2>Administrator CAC sign-in</h2>
                <p className="muted">
                  {adminIdentityEmail ? (
                    <>
                      <strong>{adminIdentityEmail}</strong> was routed here as the registered PMHub
                      root admin email.
                    </>
                  ) : (
                    'Continue with CAC to reach the PMHub administration shell.'
                  )}{' '}
                  Tester sign-in and Request access remain on the shared public host.
                </p>
              </div>

              <div className="auth-form-stack">
                <button
                  type="button"
                  className="btn primary"
                  disabled={!cacAvailable || busy !== null}
                  onClick={handleCacSignIn}
                >
                  {busy === 'cac' ? 'Verifying CAC…' : 'Continue with CAC'}
                </button>
              </div>

              <p className="auth-cac-footnote muted">
                {cacAvailable
                  ? 'PRIME hands off to the existing CAC sign-in flow. No CAC PIN is collected in the app UI.'
                  : 'CAC sign-in is not available in this environment right now.'}
              </p>
            </section>
          ) : (
            <section className="auth-entry-panel">
              <div className="auth-entry-copy">
                <span className="eyebrow">Find your sandbox</span>
                <h2>Route by email</h2>
                <p className="muted">
                  We will send you to the correct private DHA PRIME sandbox before you sign in.
                </p>
              </div>

              <form className="auth-form-stack" onSubmit={handleFrontDoorSubmit}>
                <label className="form-field">
                  <span className="form-label">Email address</span>
                  <input
                    className="form-input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="username"
                    autoFocus
                  />
                </label>
                <button className="btn primary" type="submit" disabled={busy !== null}>
                  {busy === 'frontdoor' ? 'Opening sandbox…' : 'Continue to my sandbox'}
                </button>
              </form>

              <div className="auth-support-note">
                <strong>Need help?</strong>
                <span>
                  If your email is not recognized, contact the UAT coordinator to confirm your
                  sandbox assignment or the registered PMHub root admin email.
                </span>
              </div>
            </section>
          )}

          {err ? <div className="banner red">{err.message}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="persona-login-shell">
      {summoning}
      <div className="persona-login-hero">
        {/* Held still — see the note on the front-door hero above. */}
        <div className="persona-login-hero-inner">
          <div className="login-brand">
            {/* PRIME is the primary brand (PRIME-BRAND-UX-001); the DHA emblem is
              a secondary affiliation below the headline. */}
            <LoginBrandLockup
              kicker="Portfolio Command · FY26"
              slotRef={brandSlotRef}
              markVisible={markVisible}
            />
            <div className="login-affiliation" data-testid="login-affiliation">
              <span className="login-brand-mark login-brand-seal">
                <img
                  src="/brand/dha-seal.svg"
                  alt="Defense Health Agency seal"
                  data-testid="login-dha-seal"
                />
              </span>
              <span className="login-affiliation-text">Defense Health Agency</span>
            </div>
          </div>
          <p className="login-blurb">
            Executive command center for the DHA portfolio. Mission-aligned investment, sprint &amp;
            gate-level delivery, AI-assisted decision support — all grounded in audited evidence.
          </p>
          <ul className="login-bullets">
            <li>
              {sharedLiveMode
                ? 'Everyone signs into the same PRIME application from this page'
                : passwordOnlySandboxLogin
                  ? 'Password-only sign-in for your private sandbox — no CAC needed here'
                  : isProductionBuild
                    ? 'One PRIME sign-in page for password and CAC access'
                    : 'Local launch workspace with role-aware visibility and auth-path validation'}
            </li>
            <li>
              {personaShortcutsEnabled && passwordOnlySandboxLogin
                ? 'Persona shortcuts let you experience PRIME from each role inside this sandbox'
                : personaShortcutsEnabled
                  ? 'Persona shortcuts remain available only in local and demo environments'
                  : 'Password reset and password issuance stay under System Admin control'}
            </li>
            <li>Approved users sign in with the email address on their PRIME account</li>
          </ul>
        </div>
      </div>

      <div className="persona-login-card">
        {sandboxMode === 'sandbox' ? (
          <div className="banner blue" data-testid="private-sandbox-notice">
            {sandboxInstance?.label || 'Private UAT sandbox'} is isolated for your testing run.
            Records, reset actions, and activity history stay inside this sandbox.
          </div>
        ) : null}
        <div className="auth-entry-grid">
          <section className="auth-entry-panel">
            <div className="auth-entry-copy">
              <span className="eyebrow">Primary sign-in</span>
              <h2>Work email and password</h2>
              <p className="muted">
                Use the temporary or active password issued by your System Admin. Password resets
                stay admin-managed in this release.
              </p>
            </div>

            <form className="auth-form-stack" onSubmit={handlePasswordSignIn}>
              {passwordOnlySandboxLogin && passIdentity?.email ? (
                <p className="muted" data-testid="sandbox-signing-in-as">
                  Signing in as <strong>{passIdentity.email}</strong>.
                </p>
              ) : passwordOnlySandboxLogin ? (
                // No verified routing-pass identity (e.g. returning to sign-in
                // after a persona session). Without an email field this form
                // could never submit — the routed identity is gone (DEF-006).
                <label className="form-field">
                  <span className="form-label">Email address</span>
                  <input
                    className="form-input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="username"
                    autoFocus
                    data-testid="sandbox-email-fallback"
                  />
                </label>
              ) : (
                <label className="form-field">
                  <span className="form-label">Email address</span>
                  <input
                    className="form-input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="username"
                    autoFocus
                  />
                </label>
              )}
              <label className="form-field">
                <span className="form-label">Password</span>
                <input
                  className="form-input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your issued password"
                  autoComplete="current-password"
                  autoFocus={passwordOnlySandboxLogin && Boolean(passIdentity?.email)}
                />
              </label>
              <button className="btn primary" type="submit" disabled={busy !== null}>
                {busy === 'password' ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <div className="auth-support-note">
              <strong>Contact your System Admin</strong>
              <span>
                {passwordOnlySandboxLogin
                  ? ' if you need a first password, a reset, or your PRIME account to be created. PMHub UAT sandboxes do not use CAC sign-in on this page.'
                  : ' if you need a first password, a reset, or your PRIME account to be created.'}
              </span>
            </div>
          </section>

          {passwordOnlySandboxLogin ? null : (
            <section className="auth-entry-panel auth-entry-panel--alternate">
              <div className="auth-entry-copy">
                <span className="eyebrow">Alternate sign-in</span>
                <h2>CAC sign-in</h2>
                <p className="muted">
                  CAC remains available for linked PRIME accounts and routes to the same identity,
                  roles, and activity history.
                </p>
              </div>
              <button
                type="button"
                className="btn ghost"
                disabled={!cacAvailable || busy !== null}
                onClick={handleCacSignIn}
              >
                {busy === 'cac' ? 'Verifying CAC…' : 'Sign in with CAC'}
              </button>
              <p className="auth-cac-footnote muted">
                {cacAvailable
                  ? 'Use this option when your CAC identity is already linked to a PRIME user.'
                  : 'CAC sign-in is not available in this environment right now.'}
              </p>
            </section>
          )}
        </div>

        {personaShortcutsEnabled ? (
          <section className="auth-persona-shortcuts">
            <div className="auth-entry-copy">
              <span className="eyebrow">
                {passwordOnlySandboxLogin ? 'Sandbox personas' : 'Local and demo only'}
              </span>
              <h2>Persona shortcuts</h2>
              <p className="muted">
                {passwordOnlySandboxLogin
                  ? 'Step into a role to experience PRIME from that seat — a guided-UAT shortcut ' +
                    'that stays inside your private sandbox. Your own account remains the ' +
                    'primary sign-in above.'
                  : 'These shortcuts stay available only for local/demo validation and never ' +
                    'render on the real password-first sign-in experience.'}
              </p>
            </div>
            <div className="persona-grid">
              {PERSONAS.map((p) => {
                const def = rbac.PERSONA_DISPLAY[p.key];
                return (
                  <button
                    key={p.key}
                    className={`persona-card ${def.accent}`}
                    data-testid={`persona-card-${p.key}`}
                    onClick={() => enter(p.key)}
                    disabled={busy && busy !== `persona:${p.key}`}
                  >
                    <div className="persona-card-head">
                      <div className={`persona-swatch ${def.accent}`} aria-hidden />
                      <div className="persona-card-titles">
                        <div className="persona-card-label">{def.label}</div>
                        {def.personName ? (
                          <div
                            className="persona-card-person"
                            data-testid={`persona-card-person-${p.key}`}
                          >
                            {def.personName}
                          </div>
                        ) : null}
                        <div className="persona-card-summary">{def.summary}</div>
                      </div>
                    </div>
                    <div className="persona-card-meta">{p.highlight}</div>
                    <span className="btn primary sm">
                      {busy === `persona:${p.key}` ? 'Entering…' : 'Enter as this user'}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {err ? <div className="banner red">{err.message}</div> : null}
      </div>
    </div>
  );
}
